import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../atoms/button";
import Loading from "../atoms/loading";
import BotOpsLiveShell from "./botOpsLiveShell";
import BotOpsSandboxModelPicker from "./botOpsSandboxModelPicker";
import {
    getSandboxSession,
    createSandboxSession,
    resetSandboxSession,
    sendSandboxMessage,
    listSandboxMessages,
    listPromptfooRuns,
    getSandboxKill,
    getSandboxModel,
    putSandboxModel,
    getModelsCatalog,
} from "./functions";

const POLL_INTERVAL_MS = 2000;
const POLL_BACKOFF_MS = 15000;
const REPLY_TIMEOUT_MS = 45000;
const FLOW_RUN_POLL_MS = 5000;
const ACTIVE_FLOW_STATUSES = ["queued", "running"];

/** Strings com escapes Unicode — evita mojibake se o arquivo for salvo com encoding errado. */
const TXT = {
    you: "Voc\u00ea",
    noWhatsapp: "n\u00e3o entrega WhatsApp",
    sessionLoadErr: "Erro ao carregar sess\u00e3o sandbox",
    sessionStartErr: "Erro ao iniciar sess\u00e3o",
    sessionStarted: "Sess\u00e3o sandbox iniciada",
    sessionHint:
        "Inicie a sess\u00e3o na inbox sandbox dedicada (contato \u00fanico) para testar prompt, knowledge e guardrails sem chip WhatsApp.",
    starting: "Iniciando\u2026",
    startSession: "Iniciar sess\u00e3o",
    testingPlaceholder: "Digite sua mensagem de teste\u2026",
    sending: "Enviando\u2026",
    killActive:
        "Kill do sandbox ativo \u2014 chat de teste sem turno IA (sem fila Triagem).",
    humanOwnerBanner:
        "Dono da conversa: humano. O bot n\u00e3o responde at\u00e9 voc\u00ea clicar em Nova conversa (thread nova com dono bot).",
    reopenedNotice:
        "Conversa reaberta no Chatwoot. Se o dono ainda for humano, o bot continua mudo \u2014 use Nova conversa.",
    subtitle:
        "Cada aba tem conversa pr\u00f3pria na inbox sandbox \u2014 Nova conversa / Testes n\u00e3o misturam threads",
    resetOk: "Nova conversa sandbox criada",
    resetErr: "Erro ao criar nova conversa",
    newConversation: "Nova conversa",
    timeout:
        "Sem resposta do bot em 45s. Se o dono for humano, clique em Nova conversa; sen\u00e3o verifique o Chatwoot ou o card Funcionamento.",
    fetchLoop:
        "Falha ao falar com a API do sandbox. Tentando de novo automaticamente\u2026",
    flowInfoBanner:
        "H\u00e1 um cen\u00e1rio em execu\u00e7\u00e3o em Testes \u2014 ele usa outra conversa; esta janela continua isolada.",
};

function normalizePromptfooRuns(data) {
    if (Array.isArray(data)) return data;
    if (data?.runs && Array.isArray(data.runs)) return data.runs;
    return [];
}

function snapshotHasFlow(snapshot) {
    const cases = snapshot?.cases;
    if (!Array.isArray(cases)) return false;
    return cases.some((c) => c?.kind === "flow");
}

/**
 * Run ativa em Testes (informativo no chat).
 * Com isolamento por janela o chat não pausa mais o envio.
 */
export function isFlowRunLocking(run) {
    if (!run || !ACTIVE_FLOW_STATUSES.includes(run.status)) return false;
    return Boolean(run.progress) || snapshotHasFlow(run.suite_snapshot);
}

function findLockingFlowRun(runs) {
    const list = Array.isArray(runs) ? runs : [];
    return list.find(isFlowRunLocking) || null;
}

const mergeMessages = (prev, incoming) => {
    if (!incoming?.length) return prev;
    const byId = new Map(prev.map((m) => [m.id, m]));
    for (const msg of incoming) {
        byId.set(msg.id, msg);
    }
    return Array.from(byId.values()).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
};

/** Distância do fim da thread em que ainda consideramos "colado no fundo". */
export const CHAT_NEAR_BOTTOM_PX = 96;

export function isNearBottom(el, thresholdPx = CHAT_NEAR_BOTTOM_PX) {
    if (!el) return true;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    return gap <= thresholdPx;
}

const formatMsgTime = (ts) => {
    if (!ts) return null;
    try {
        const d = typeof ts === "number" ? new Date(ts * (ts < 1e12 ? 1000 : 1)) : new Date(ts);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return null;
    }
};

const STATUS_LABEL = {
    open: "aberta",
    pending: "pendente",
    resolved: "resolvida",
    snoozed: "adiada",
};

const SessionStatusBar = ({ session }) => {
    if (!session?.conversation_id) return null;
    const statusKey = String(session.status || "")
        .trim()
        .toLowerCase();
    const statusLabel = STATUS_LABEL[statusKey] || statusKey || "\u2014";
    const owner =
        session.owner_mode === "human"
            ? "humano"
            : session.owner_mode === "bot"
              ? "bot"
              : null;
    const assigned = session.assigned || Boolean(session.assignee);

    return (
        <div
            className="flex flex-wrap gap-1.5"
            data-testid="sandbox-session-status"
        >
            <span className="rounded-md border border-emerald-700/40 bg-emerald-950/40 px-2 py-0.5 text-[10px] text-emerald-200">
                status: {statusLabel}
            </span>
            {owner ? (
                <span
                    className={`rounded-md border px-2 py-0.5 text-[10px] ${
                        owner === "humano"
                            ? "border-sky-600/40 bg-sky-950/40 text-sky-200"
                            : "border-[#d59106]/40 bg-[#d59106]/10 text-amber-200"
                    }`}
                >
                    dono: {owner}
                </span>
            ) : null}
            <span
                className={`rounded-md border px-2 py-0.5 text-[10px] ${
                    assigned
                        ? "border-sky-600/40 bg-sky-950/40 text-sky-200"
                        : "border-amber-700/40 bg-amber-950/30 text-amber-200"
                }`}
            >
                {assigned
                    ? `designada${session.assignee ? `: ${session.assignee}` : ""}`
                    : "sem designa\u00e7\u00e3o"}
            </span>
            {session.team ? (
                <span className="rounded-md border border-gray-600/50 bg-gray-800/60 px-2 py-0.5 text-[10px] text-gray-300">
                    equipe: {session.team}
                </span>
            ) : null}
            {session.inbox ? (
                <span className="rounded-md border border-gray-600/50 bg-gray-800/60 px-2 py-0.5 text-[10px] text-gray-300">
                    inbox: {session.inbox}
                </span>
            ) : null}
            {session.handoff_reason ? (
                <span className="rounded-md border border-orange-600/40 bg-orange-950/40 px-2 py-0.5 text-[10px] text-orange-200">
                    handoff: {session.handoff_reason}
                </span>
            ) : null}
        </div>
    );
};

const MessageBubble = ({ message }) => {
    const isIncoming = message.message_type === "incoming";
    const isSystem = message.message_type === "system" || message.sender_kind === "system";
    const time = formatMsgTime(message.created_at);

    if (isSystem) {
        return (
            <div
                className="my-2 flex justify-center"
                data-testid={`msg-${message.id}`}
            >
                <span className="rounded-full border border-gray-700/80 bg-gray-800/60 px-3 py-1 text-[11px] text-gray-400">
                    {message.content}
                </span>
            </div>
        );
    }

    return (
        <div
            className={`mb-2.5 flex ${isIncoming ? "justify-end" : "justify-start"}`}
            data-testid={`msg-${message.id}`}
        >
            <div
                className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                    isIncoming
                        ? "rounded-br-md bg-gradient-to-br from-[#d59106] to-[#b47c06] text-black"
                        : "rounded-bl-md border border-gray-700/80 bg-gray-800/90 text-gray-100"
                }`}
            >
                <div className="mb-0.5 flex items-center justify-between gap-3">
                    <p className={`text-[10px] font-medium uppercase tracking-wide ${
                        isIncoming ? "opacity-70" : "text-gray-400"
                    }`}>
                        {isIncoming ? TXT.you : "Bot"}
                    </p>
                    {time ? (
                        <p className={`text-[10px] ${isIncoming ? "opacity-60" : "text-gray-500"}`}>
                            {time}
                        </p>
                    ) : null}
                </div>
                <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
            </div>
        </div>
    );
};

const BotOpsSandboxChat = ({
    token,
    sandboxEnabled: sandboxEnabledProp,
    onToastGood,
    onToastBad,
    isActive,
}) => {
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionBusy, setSessionBusy] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [lockingFlowRun, setLockingFlowRun] = useState(null);
    const [sandboxEnabled, setSandboxEnabled] = useState(
        typeof sandboxEnabledProp === "boolean" ? sandboxEnabledProp : true
    );
    const [catalog, setCatalog] = useState(null);
    const [sandboxModelo, setSandboxModelo] = useState("");
    const [sandboxTemperatura, setSandboxTemperatura] = useState("");
    const [modelSaving, setModelSaving] = useState(false);

    const messagesRef = useRef(messages);
    const lastMessageIdRef = useRef(null);
    const replyTimeoutRef = useRef(null);
    const awaitingReplyRef = useRef(false);
    const outgoingCountAtSendRef = useRef(0);
    const toastBadRef = useRef(onToastBad);
    const toastGoodRef = useRef(onToastGood);
    const loadOnceRef = useRef(false);
    const pollErrorToastRef = useRef(false);
    const reopenToastRef = useRef(false);
    const threadRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const forceScrollRef = useRef(false);

    useEffect(() => {
        toastBadRef.current = onToastBad;
        toastGoodRef.current = onToastGood;
    }, [onToastBad, onToastGood]);

    useEffect(() => {
        if (typeof sandboxEnabledProp === "boolean") {
            setSandboxEnabled(sandboxEnabledProp);
        }
    }, [sandboxEnabledProp]);

    useEffect(() => {
        if (typeof sandboxEnabledProp === "boolean") return undefined;
        let cancelled = false;
        (async () => {
            const data = await getSandboxKill(token);
            if (cancelled) return;
            if (typeof data?.enabled === "boolean") {
                setSandboxEnabled(data.enabled);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, sandboxEnabledProp]);

    const loadModelAndCatalog = useCallback(async () => {
        const [catalogData, modelData] = await Promise.all([
            getModelsCatalog(token),
            getSandboxModel(token),
        ]);

        if (catalogData?.type === "error") {
            toastBadRef.current?.(catalogData.message || "Erro ao carregar catálogo de modelos");
        } else if (catalogData?.recommended) {
            setCatalog(catalogData);
        }

        if (modelData?.type === "error") {
            toastBadRef.current?.(modelData.message || "Erro ao ler modelo do sandbox");
        } else if (modelData) {
            setSandboxModelo(modelData.modelo ?? "");
            setSandboxTemperatura(
                modelData.temperatura == null ? "" : String(modelData.temperatura)
            );
        }
    }, [token]);

    useEffect(() => {
        loadModelAndCatalog();
    }, [loadModelAndCatalog]);

    const handleModelSave = async ({ modelo, temperatura }) => {
        setModelSaving(true);
        const data = await putSandboxModel(token, { modelo, temperatura });
        setModelSaving(false);

        if (data?.type === "error") {
            toastBadRef.current?.(data.message || "Erro ao salvar modelo do sandbox");
            return;
        }

        setSandboxModelo(data.modelo ?? modelo ?? "");
        setSandboxTemperatura(
            data.temperatura == null ? "" : String(data.temperatura ?? temperatura ?? "")
        );
        toastGoodRef.current?.("Modelo do sandbox salvo");
    };

    useEffect(() => {
        messagesRef.current = messages;
        const last = messages[messages.length - 1];
        lastMessageIdRef.current = last?.id ?? null;
    }, [messages]);

    const scrollThreadToBottom = useCallback((behavior = "smooth") => {
        const el = threadRef.current;
        if (!el) return;
        // scrollTop direto cobre browsers sem scrollTo suave; preferimos o fundo absoluto.
        if (typeof el.scrollTo === "function") {
            el.scrollTo({ top: el.scrollHeight, behavior });
        } else {
            el.scrollTop = el.scrollHeight;
        }
    }, []);

    const onThreadScroll = useCallback(() => {
        stickToBottomRef.current = isNearBottom(threadRef.current);
    }, []);

    // Nova mensagem: acompanha o fundo se o usuário já estava colado (ou forçou ao enviar).
    useEffect(() => {
        if (!messages.length) return undefined;
        if (!forceScrollRef.current && !stickToBottomRef.current) return undefined;
        forceScrollRef.current = false;
        stickToBottomRef.current = true;
        const id = requestAnimationFrame(() => {
            scrollThreadToBottom(messages.length <= 1 ? "auto" : "smooth");
        });
        return () => cancelAnimationFrame(id);
    }, [messages, scrollThreadToBottom]);

    const clearReplyTimeout = useCallback(() => {
        if (replyTimeoutRef.current) {
            clearTimeout(replyTimeoutRef.current);
            replyTimeoutRef.current = null;
        }
        awaitingReplyRef.current = false;
    }, []);

    const startReplyTimeout = useCallback(() => {
        clearReplyTimeout();
        outgoingCountAtSendRef.current = messagesRef.current.filter(
            (m) => m.message_type === "outgoing"
        ).length;
        awaitingReplyRef.current = true;
        replyTimeoutRef.current = setTimeout(() => {
            if (!awaitingReplyRef.current) return;
            const outgoingNow = messagesRef.current.filter(
                (m) => m.message_type === "outgoing"
            ).length;
            if (outgoingNow <= outgoingCountAtSendRef.current) {
                toastBadRef.current?.(TXT.timeout);
            }
            awaitingReplyRef.current = false;
        }, REPLY_TIMEOUT_MS);
    }, [clearReplyTimeout]);

    const applyMessages = useCallback(
        (newMessages) => {
            if (!newMessages?.length) return;
            setMessages((prev) => {
                const merged = mergeMessages(prev, newMessages);
                if (awaitingReplyRef.current) {
                    const prevOutgoing = prev.filter((m) => m.message_type === "outgoing").length;
                    const mergedOutgoing = merged.filter(
                        (m) => m.message_type === "outgoing"
                    ).length;
                    if (mergedOutgoing > prevOutgoing) {
                        clearReplyTimeout();
                    }
                }
                return merged;
            });
        },
        [clearReplyTimeout]
    );

    const loadSession = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        const data = await getSandboxSession(token);
        setLoading(false);

        if (data?.type === "error") {
            setFetchError(data.message || TXT.sessionLoadErr);
            // Um único toast — não depende de badToast no useEffect (evita loop)
            toastBadRef.current?.(data.message || TXT.sessionLoadErr);
            return;
        }

        if (data) {
            setSession(data);
            const listData = await listSandboxMessages(token);
            if (listData?.type === "error") {
                setFetchError(listData.message || "Erro ao carregar mensagens");
                toastBadRef.current?.(listData.message || "Erro ao carregar mensagens");
            } else if (Array.isArray(listData?.messages)) {
                setMessages(listData.messages);
            }
        } else {
            setSession(null);
            setMessages([]);
        }
    }, [token]);

    useEffect(() => {
        if (loadOnceRef.current) return undefined;
        loadOnceRef.current = true;
        loadSession();
        return undefined;
    }, [loadSession]);

    useEffect(() => {
        return () => clearReplyTimeout();
    }, [clearReplyTimeout]);

    useEffect(() => {
        const conversationId = session?.conversation_id;
        // NÃO gatear em fetchError: setFetchError no dep matava o loop (cleanup
        // cancelava o backoff e o effect saía cedo). Erro transitório → backoff + retry.
        if (!isActive || !conversationId) return undefined;

        let cancelled = false;
        let timer = null;
        let delay = POLL_INTERVAL_MS;
        let consecutiveErrors = 0;

        const schedule = (ms) => {
            if (cancelled) return;
            timer = setTimeout(runPoll, ms);
        };

        const runPoll = async () => {
            const afterId = lastMessageIdRef.current;
            const data = await listSandboxMessages(token, afterId);
            if (cancelled) return;
            if (data?.type === "error") {
                consecutiveErrors += 1;
                setFetchError(data.message || TXT.fetchLoop);
                delay =
                    consecutiveErrors >= 2 ? POLL_BACKOFF_MS : POLL_INTERVAL_MS;
                if (!pollErrorToastRef.current) {
                    pollErrorToastRef.current = true;
                    toastBadRef.current?.(data.message || TXT.fetchLoop);
                }
                schedule(delay);
                return;
            }
            consecutiveErrors = 0;
            pollErrorToastRef.current = false;
            setFetchError(null);
            delay = POLL_INTERVAL_MS;
            // Ignora poll de outra conversa (corrida após Nova conversa / isolamento).
            if (
                data?.conversation_id != null &&
                conversationId != null &&
                String(data.conversation_id) !== String(conversationId)
            ) {
                schedule(delay);
                return;
            }
            if (data?.owner_mode != null || data?.status != null || data?.assignee != null || data?.team != null) {
                setSession((prev) =>
                    prev
                        ? {
                              ...prev,
                              owner_mode: data.owner_mode ?? prev.owner_mode,
                              status: data.status ?? prev.status,
                              handoff_reason:
                                  data.handoff_reason ?? prev.handoff_reason,
                              assignee: data.assignee ?? prev.assignee,
                              team: data.team ?? prev.team,
                              inbox: data.inbox ?? prev.inbox,
                              assigned:
                                  data.assigned != null
                                      ? data.assigned
                                      : data.assignee != null
                                        ? Boolean(data.assignee)
                                        : prev.assigned,
                          }
                        : prev
                );
            }
            const incoming = data?.messages || [];
            const reopen = incoming.find(
                (m) =>
                    (m.message_type === "activity" || m.sender_kind === "system") &&
                    /reaberta/i.test(String(m.content || ""))
            );
            if (reopen && !reopenToastRef.current) {
                reopenToastRef.current = true;
                toastBadRef.current?.(TXT.reopenedNotice);
            }
            applyMessages(incoming);
            schedule(delay);
        };

        runPoll();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [isActive, session?.conversation_id, token, applyMessages]);

    useEffect(() => {
        if (!isActive || !token) return undefined;

        let cancelled = false;
        let timer = null;

        const refreshFlowLock = async () => {
            const data = await listPromptfooRuns(token);
            if (cancelled) return;
            if (data?.type === "error") {
                setLockingFlowRun(null);
            } else {
                setLockingFlowRun(findLockingFlowRun(normalizePromptfooRuns(data)));
            }
            if (!cancelled) {
                timer = setTimeout(refreshFlowLock, FLOW_RUN_POLL_MS);
            }
        };

        refreshFlowLock();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [isActive, token]);

    const handleRetry = async () => {
        pollErrorToastRef.current = false;
        loadOnceRef.current = false;
        await loadSession();
        loadOnceRef.current = true;
    };

    const handleStartSession = async () => {
        setSessionBusy(true);
        setFetchError(null);
        const data = await createSandboxSession(token);
        setSessionBusy(false);

        if (data?.type === "error") {
            setFetchError(data.message || TXT.sessionStartErr);
            toastBadRef.current?.(data.message || TXT.sessionStartErr);
            return;
        }

        setSession(data);
        setMessages([]);
        toastGoodRef.current?.(TXT.sessionStarted);
    };

    const handleNewConversation = async () => {
        setSessionBusy(true);
        clearReplyTimeout();
        setFetchError(null);
        const data = await resetSandboxSession(token);
        setSessionBusy(false);

        if (data?.type === "error") {
            setFetchError(data.message || TXT.resetErr);
            toastBadRef.current?.(data.message || TXT.resetErr);
            return;
        }

        setSession(data);
        setMessages([]);
        toastGoodRef.current?.(TXT.resetOk);
    };

    const handleSend = async () => {
        const content = draft.trim();
        if (!content || !session?.conversation_id || sending) return;

        setSending(true);
        const data = await sendSandboxMessage(token, content);
        setSending(false);

        if (data?.type === "error") {
            toastBadRef.current?.(data.message || "Erro ao enviar mensagem");
            return;
        }

        setMessages((prev) => mergeMessages(prev, [data]));
        setDraft("");
        forceScrollRef.current = true;
        stickToBottomRef.current = true;
        startReplyTimeout();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (loading) {
        return <Loading classes="m-auto mt-4" />;
    }

    const conversationId = session?.conversation_id;
    const flowRunning = Boolean(lockingFlowRun);
    // Chat desta janela: sempre a própria conversa (não a do flow em Testes).
    const shellMode = "free";
    const shellProgress = null;
    const traceConversationId = conversationId;
    const conversationLive = conversationId
        ? {
              status: session?.status ?? null,
              owner_mode: session?.owner_mode ?? null,
              assignee: session?.assignee ?? null,
              team: session?.team ?? null,
              inbox: session?.inbox ?? null,
              assigned: session?.assigned ?? null,
              handoff_reason: session?.handoff_reason ?? null,
          }
        : null;

    const chatThreadAndComposer = (
        <>
            <div
                ref={threadRef}
                onScroll={onThreadScroll}
                className="bot-ops-chat-thread relative min-h-[280px] max-h-[min(60vh,520px)] overflow-y-auto overscroll-contain rounded-xl border border-[#b47c06]/40 bg-[#0c0c0c] p-3"
                aria-label="Mensagens do chat sandbox"
                data-testid="sandbox-chat-thread"
            >
                {messages.length === 0 ? (
                    <p className="py-12 text-center text-sm text-gray-400">
                        Envie uma mensagem para testar o bot.
                    </p>
                ) : (
                    <div className="flex flex-col gap-0.5">
                        {messages.map((msg) => (
                            <MessageBubble key={msg.id} message={msg} />
                        ))}
                    </div>
                )}
            </div>

            <div className="rounded-xl border border-gray-700/70 bg-gray-900/50 p-2.5">
                <div className="flex flex-wrap items-end gap-2">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={2}
                        placeholder={TXT.testingPlaceholder}
                        className="min-w-[200px] flex-1 rounded-lg border border-gray-600/50 bg-white p-2.5 text-sm text-black placeholder:text-gray-500 focus:border-[#d59106] focus:outline-none focus:ring-1 focus:ring-[#d59106]/40"
                        disabled={sending || sessionBusy}
                    />
                    <div className="flex gap-2">
                        <Button
                            click={handleSend}
                            disabled={
                                sending ||
                                sessionBusy ||
                                !draft.trim()
                            }
                            classes="bg-[#d59106] hover:bg-[#b47c06] px-4 py-2 rounded-lg font-medium"
                        >
                            {sending ? TXT.sending : "Enviar"}
                        </Button>
                        <Button
                            click={handleNewConversation}
                            disabled={sessionBusy || sending}
                            classes="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg font-medium text-white"
                        >
                            {TXT.newConversation}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold tracking-tight text-laranjinha">
                        Chat de teste
                    </h2>
                    <p className="text-xs text-gray-400">{TXT.subtitle}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md border border-amber-600/50 bg-amber-900/40 px-2 py-0.5 text-xs text-amber-200">
                        {TXT.noWhatsapp}
                    </span>
                    {conversationId ? (
                        <span className="rounded-md border border-gray-600/60 bg-gray-800/70 px-2 py-0.5 font-mono text-xs text-gray-300">
                            #{conversationId}
                        </span>
                    ) : null}
                </div>
            </div>

            {conversationId ? <SessionStatusBar session={session} /> : null}

            {catalog ? (
                <BotOpsSandboxModelPicker
                    catalog={catalog}
                    initialModelo={sandboxModelo}
                    initialTemperatura={sandboxTemperatura}
                    saving={modelSaving}
                    onSave={handleModelSave}
                />
            ) : null}

            {fetchError ? (
                <div
                    className="rounded-lg border border-red-500/60 bg-red-950/40 p-3 text-sm text-red-200 flex flex-wrap items-center justify-between gap-2"
                    role="alert"
                >
                    <span>{fetchError}</span>
                    <Button
                        click={handleRetry}
                        disabled={sessionBusy}
                        classes="bg-red-700 hover:bg-red-600 px-3 py-1 text-xs rounded-md"
                    >
                        Tentar de novo
                    </Button>
                </div>
            ) : null}

            {!sandboxEnabled ? (
                <div
                    className="rounded-lg border border-amber-500/60 bg-amber-950/40 p-3 text-sm text-amber-200"
                    role="alert"
                    data-testid="sandbox-kill-banner"
                >
                    {TXT.killActive}
                </div>
            ) : null}

            {session?.owner_mode === "human" ? (
                <div
                    className="rounded-lg border border-orange-500/60 bg-orange-950/40 p-3 text-sm text-orange-100"
                    role="alert"
                    data-testid="sandbox-human-owner-banner"
                >
                    {TXT.humanOwnerBanner}
                </div>
            ) : null}

            {flowRunning ? (
                <div
                    className="rounded-lg border border-sky-500/60 bg-sky-950/40 p-3 text-sm text-sky-100"
                    role="status"
                    data-testid="sandbox-flow-info"
                >
                    {TXT.flowInfoBanner}
                </div>
            ) : null}

            {!conversationId ? (
                <div className="rounded-xl border border-dashed border-[#b47c06]/70 bg-gradient-to-b from-gray-900/40 to-transparent py-10 text-center">
                    <p className="mx-auto mb-4 max-w-md text-sm text-gray-300">{TXT.sessionHint}</p>
                    <Button
                        click={handleStartSession}
                        disabled={sessionBusy}
                        classes="bg-[#d59106] hover:bg-[#b47c06] px-5 py-2.5 rounded-lg font-medium"
                    >
                        {sessionBusy ? TXT.starting : TXT.startSession}
                    </Button>
                </div>
            ) : (
                <BotOpsLiveShell
                    mode={shellMode}
                    conversationId={traceConversationId}
                    locked={false}
                    progress={shellProgress}
                    token={token}
                    isActive={isActive}
                    ownerFallback={session?.owner_mode || null}
                    conversationLive={conversationLive}
                    onToastBad={onToastBad}
                    childrenChat={chatThreadAndComposer}
                />
            )}
        </div>
    );
};

export default BotOpsSandboxChat;
